import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiUploadCloud, FiLayers } from "react-icons/fi";
import DraggableList from "components/draggable/DraggableList";
import Template from "components/draggable/Template";
import useMainContent from "hooks/main/useMainContent";
import MainContentDialog from "components/main-content/MainContentDialog";

const MainContent = ({
  items,
  id,
  label,
  isLoading,
  getNextPage,
  page,
  setPage,
  hasMoreItems,
  category,
  setCategory,
  categories,
  cardComponent,
}) => {
  const { t } = useTranslation();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const { content, updateContent } = useMainContent();
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (content) {
      const match = content.find((i) => i.id === id);
      setSelectedItems(match?.content || []);
    }
  }, [content, id]);

  const hasUnsavedChanges =
    JSON.stringify(selectedItems) !==
    JSON.stringify(content?.find((i) => i.id === id)?.content || []);

  const handlePublish = async () => {
    await updateContent(id, selectedItems);
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-7 shadow-xs">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
            <FiLayers size={20} />
          </span>
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-base sm:text-lg font-bold text-slate-900">
                {label}
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                {selectedItems.length} նյութ
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Քաշեք և գցեք տարրերը՝ գլխավոր էջում դրանց հերթականությունը փոխելու համար:
            </p>
          </div>
        </div>

        {/* Publish Action Button */}
        {hasUnsavedChanges && (
          <button
            type="button"
            onClick={handlePublish}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white shadow-xs transition cursor-pointer self-start sm:self-auto animate-pulse"
          >
            <FiUploadCloud size={15} />
            <span>{t("main.publish") || "Հրապարակել փոփոխությունները"}</span>
          </button>
        )}
      </div>

      {/* Draggable List or Empty State */}
      <div className="pt-4">
        {selectedItems.length > 0 ? (
          <DraggableList
            list={selectedItems}
            handleStateChange={(newOrder) => setSelectedItems(newOrder)}
            deleteItem={(deletedId) =>
              setSelectedItems(selectedItems.filter((i) => i.id !== deletedId))
            }
            template={<Template />}
          />
        ) : (
          <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-2xl my-2">
            <p className="text-xs text-slate-500 font-medium">
              Այս բաժնում դեռ ընտրված նյութեր չկան:
            </p>
          </div>
        )}

        {/* Add Button */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setIsOpenDialog(true)}
            className="w-full py-3 px-4 rounded-2xl border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/30 text-slate-600 hover:text-indigo-600 font-semibold text-xs transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <FiPlus size={16} />
            <span>Ավելացնել նյութեր {label} բաժնում</span>
          </button>
        </div>
      </div>

      {/* Picker Dialog */}
      <MainContentDialog
        id={id}
        open={isOpenDialog}
        onClose={() => setIsOpenDialog(false)}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        isLoading={isLoading}
        hasMoreItems={hasMoreItems}
        getNextPage={getNextPage}
        page={page}
        setPage={setPage}
        label={label}
        category={category}
        setCategory={setCategory}
        categories={categories}
        cardComponent={cardComponent}
      />
    </div>
  );
};

export default MainContent;
